'use client';

import React, { useState } from 'react';
import { signIn, signUp, signInWithGoogle, type AuthSession } from '../lib/supabase/client';
import { useLocale } from '../lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/sonner';
import { X } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (session: AuthSession) => void;
}

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const { t, locale } = useLocale();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      if (mode === 'signin') {
        const res = await signIn(email, password);
        if (res.error) {
          setError(res.error);
          toast.error(res.error);
        } else if (res.session) {
          toast.success(locale === 'vi' ? 'Đăng nhập thành công!' : 'Signed in successfully!');
          onSuccess?.(res.session);
          onClose();
        }
      } else {
        const res = await signUp(email, password);
        if (res.error) {
          setError(res.error);
          toast.error(res.error);
        } else {
          const successMsg = locale === 'vi' ? 'Đã tạo tài khoản! Bạn có thể đăng nhập ngay bây giờ.' : 'Account created! You can now sign in.';
          setInfo(successMsg);
          toast.success(successMsg);
          setMode('signin');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in-0 duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="w-full max-w-[420px] bg-card border border-border rounded-lg p-7 shadow-2xl text-card-foreground">
        <div className="flex justify-between items-center mb-5">
          <h2 id="auth-modal-title" className="text-xl font-bold tracking-tight text-foreground m-0">
            {mode === 'signin' ? t('auth.sign_in_title') : t('auth.sign_up_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('auth.close')}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-sm"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Google OAuth Button */}
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setLoading(true);
            signInWithGoogle();
          }}
          disabled={loading}
          className="w-full min-h-[44px] gap-3 font-semibold mb-1"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17Z"/>
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.26v3.15C3.25 21.36 7.31 24 12 24Z"/>
            <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.26C.46 8.16 0 9.94 0 12s.46 3.84 1.26 5.42l4.02-3.15Z"/>
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.25 2.64 1.26 6.58l4.02 3.15c.95-2.83 3.6-4.98 6.72-4.98Z"/>
          </svg>
          <span>{t('auth.continue_with_google')}</span>
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">{t('auth.or_divider')}</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {info && (
          <Alert variant="success" className="mb-4">
            <AlertDescription>{info}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="grid gap-3.5">
          <div>
            <label htmlFor="auth-email" className="block text-xs font-semibold mb-1.5 text-muted-foreground">
              {t('auth.email_label')}
            </label>
            <Input
              id="auth-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label htmlFor="auth-password" className="block text-xs font-semibold mb-1.5 text-muted-foreground">
              {t('auth.password_label')}
            </label>
            <Input
              id="auth-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <Button
            type="submit"
            loading={loading}
            variant="default"
            className="w-full mt-2 h-11 text-sm font-bold"
          >
            {mode === 'signin' ? t('auth.sign_in_btn') : t('auth.sign_up_btn')}
          </Button>
        </form>

        <div className="text-center mt-4">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              setError(null);
              setInfo(null);
              setMode(mode === 'signin' ? 'signup' : 'signin');
            }}
            className="text-xs text-sky-400 hover:text-sky-300"
          >
            {mode === 'signin' ? t('auth.switch_to_sign_up') : t('auth.switch_to_sign_in')}
          </Button>
        </div>
      </div>
    </div>
  );
}
